import { useEffect, useRef, useState } from 'react'
import {
  endpoints,
  type BoardMember, type Consult, type ConsultActionItem, type ConsultThread,
} from '../api/client'
import { Avatar } from '../components/Avatar'
import { SeatPicker } from '../components/SeatPicker'
import { Prose } from '../components/Prose'
import { Attachments } from './Attachments'

/*
  UI SPEC §5.

  Two decisions the spec forced:

  1. The default is "Add to Friday", not "Ask now". The default should feed the ritual.

  2. Submission is instant and optimistic. The sheet closes immediately and a toast
     confirms with an undo. It never blocks on the clarifier, because the owner is in a
     truck and a spinner is the thing that kills the capture loop.

  Voice is specced as the primary path but needs a transcription provider that has not been
  chosen, so the mic is present and honest about being unavailable rather than absent. A
  missing control reads as a product that never intended to have one.
*/

type Mode = 'agenda' | 'ask'

export function Capture({
  onClose, onCaptured, onAsked, board,
}: {
  onClose: () => void
  onCaptured: (text: string) => Promise<void>
  onAsked: (consultId: string) => void
  board: BoardMember[]
}) {
  const [mode, setMode] = useState<Mode>('agenda')
  const [text, setText] = useState('')
  const [busy, setBusy] = useState(false)
  const [seat, setSeat] = useState<BoardMember | null>(null)
  const [picking, setPicking] = useState(false)
  const box = useRef<HTMLTextAreaElement>(null)

  useEffect(() => { box.current?.focus() }, [])

  // Escape closes. A sheet that traps someone mid-drive is worse than no sheet.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose() }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [onClose])

  const submit = async () => {
    const value = text.trim()
    if (!value || busy) return
    setBusy(true)

    try {
      if (mode === 'agenda') {
        await onCaptured(value)
      } else {
        const { id } = await endpoints.ask(value, seat?.id)
        onAsked(id)
      }
      onClose()
    } finally {
      setBusy(false)
    }
  }

  if (picking) {
    return (
      <SeatPicker
        board={board}
        chosen={seat}
        onChoose={setSeat}
        onClose={() => setPicking(false)}
      />
    )
  }

  return (
    <div className="sheet-backdrop" onClick={onClose}>
      <div className="sheet" onClick={e => e.stopPropagation()} role="dialog" aria-label="Capture">
        <div className="mic">
          <button className="mic__button" disabled title="Voice capture is not wired up yet">
            <svg viewBox="0 0 24 24" width="28" height="28" fill="none" stroke="currentColor" strokeWidth={1.5} strokeLinecap="round">
              <rect x="9" y="3" width="6" height="11" rx="3" />
              <path d="M5 11a7 7 0 0 0 14 0M12 18v3" />
            </svg>
          </button>
          <div className="dim">Voice is coming. Type for now.</div>
        </div>

        <textarea
          ref={box}
          className="textarea"
          value={text}
          placeholder={mode === 'agenda' ? 'What is on your mind?' : 'Ask the board something'}
          onChange={e => setText(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) void submit() }}
        />

        <div className="segmented" role="group" aria-label="Where this goes">
          <button
            className="segmented__option"
            aria-pressed={mode === 'ask'}
            onClick={() => setMode('ask')}
          >
            Ask now
          </button>
          <button
            className="segmented__option"
            aria-pressed={mode === 'agenda'}
            onClick={() => setMode('agenda')}
          >
            Add to Friday
          </button>
        </div>

        <button className="btn btn--primary btn--block" disabled={!text.trim() || busy} onClick={submit}>
          {busy ? 'Sending'
            : mode === 'agenda' ? 'Add to the agenda'
            : seat ? `Ask ${seat.displayName.split(' ')[0]}`
            : 'Ask the board'}
        </button>

        {/* Quiet on purpose. The chair handling it is right nearly every time, so this is
            a line of text under the button rather than a control to operate first. */}
        {mode === 'ask' && board.length > 0 && (
          <div className="ask__who">
            {seat ? (
              <>
                <span>
                  Just {seat.displayName.split(' ')[0]}, on {seat.discipline.toLowerCase()}.
                </span>
                <button className="link" onClick={() => setSeat(null)}>Ask the chair instead</button>
              </>
            ) : (
              <>
                <span>The chair takes it.</span>
                <button className="link" onClick={() => setPicking(true)}>Ask someone in particular</button>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  )
}

/*
  §5 ambiguous routing: answer briefly, then offer escalation inline.

  The answer arrives asynchronously because the web tier cannot call Anthropic (§2.1), so
  this polls. A consult is about a minute of model time, which is why the copy says the
  board is thinking rather than showing a bare spinner.
*/
export function ConsultAnswer({
  consultId, board, onClose, onEscalated, onCommitted,
}: {
  consultId: string
  board: BoardMember[]
  onClose: () => void
  onEscalated: () => void
  /*
    The action list just changed. The shell reloads it, says so, and offers a way to go and
    look at the thing that was added, which is the whole reason the id is passed.
  */
  onCommitted: (message: string, undo: () => Promise<void>, commitmentId: string | null) => void
}) {
  const [consult, setConsult] = useState<Consult | null>(null)
  const [thread, setThread] = useState<ConsultThread | null>(null)
  const [actions, setActions] = useState<ConsultActionItem[]>([])
  const [applying, setApplying] = useState<string | null>(null)
  const [reply, setReply] = useState('')
  const [sending, setSending] = useState(false)
  const [escalating, setEscalating] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const refresh = async () => {
    const [c, t, a] = await Promise.all([
      endpoints.consult(consultId),
      endpoints.consultThread(consultId),
      endpoints.consultActions(consultId).catch(() => [] as ConsultActionItem[]),
    ])
    setConsult(c)
    setThread(t)
    setActions(a)
    return c
  }

  /*
    Putting one of the member's asks on the action list.

    Optimism would be wrong here. This writes a dated commitment the board will chase, so
    the button waits for the server and the toast that follows carries the undo, which
    deletes the commitment outright rather than resolving it: it was never something the
    owner stood behind.
  */
  const apply = async (action: ConsultActionItem) => {
    if (applying) return
    setApplying(action.id)
    setError(null)
    try {
      /*
        The endpoint has always answered with the commitment it created and this threw the
        answer away. Without it the owner is told the action was added and then has to find
        it: on a list of thirteen, a new one due in a fortnight sorts last, under five past
        due and seven nearer ones, and the Home widget only ever shows three.
      */
      const created = await endpoints.acceptAction(consultId, action.id)
      await refresh()
      onCommitted(
        `On the action list, due in ${action.dueInDays} days`,
        async () => {
          await endpoints.releaseAction(consultId, action.id)
          await refresh()
        },
        created?.id ?? null,
      )
    } catch (e) {
      setError(e instanceof Error ? e.message : 'That did not go on the action list')
    } finally {
      setApplying(null)
    }
  }

  useEffect(() => {
    let cancelled = false
    let attempts = 0

    const poll = async () => {
      if (cancelled || attempts++ > 90) return
      try {
        const c = await refresh()
        if (cancelled || c.status !== 'pending') return
      } catch {
        /* transient; keep waiting rather than showing a failure that is not one */
      }
      setTimeout(poll, 2000)
    }

    void poll()
    return () => { cancelled = true }
  }, [consultId, sending])

  // Recorded when the consult was opened, so it is available before the answer is.
  const answering = board.filter(m => consult?.membersUsed?.includes(m.id))
  const thinking = !consult || consult.status === 'pending'

  const send = async () => {
    const text = reply.trim()
    if (!text || sending) return
    setSending(true)
    setError(null)
    try {
      await endpoints.replyToConsult(consultId, text)
      setReply('')
      await refresh()
    } catch (e) {
      setError(e instanceof Error ? e.message : 'That did not send')
    } finally {
      setSending(false)
    }
  }

  return (
    <div className="sheet-backdrop" onClick={onClose}>
      <div className="sheet sheet--chat" onClick={e => e.stopPropagation()} role="dialog" aria-label="Answer">
        {consult?.status === 'failed' ? (
          <>
            <div className="card__title">That did not go through</div>
            <p className="muted" style={{ marginTop: 'var(--space-2)' }}>{consult.error}</p>
            <button className="btn btn--block" style={{ marginTop: 'var(--space-4)' }} onClick={onClose}>Close</button>
          </>
        ) : (
          <>
            {/* The same thread shape the table uses, so an exchange here reads like the
                exchange there rather than like a different product. */}
            <div className="thread">
              {(thread?.messages ?? []).map(m => {
                const who = board.find(b => b.id === m.boardMemberId) ?? null
                return (
                  <div key={m.id} className={`msg msg--${m.role === 'owner' ? 'owner' : 'board'}`}>
                    {m.role !== 'owner' && (
                      <span className="msg__avatar">
                        {who ? <Avatar member={who} size="sm" /> : <span className="avatar">BD</span>}
                      </span>
                    )}
                    <span className="msg__body">
                      <span className="msg__bubble"><Prose raw={m.body} /></span>
                      <span className="msg__by">
                        {m.role === 'owner' ? 'You' : who?.displayName ?? 'The board'}
                      </span>
                    </span>
                  </div>
                )
              })}

              {thinking && (
                <div className="msg msg--board">
                  <span className="msg__avatar">
                    {answering[0] ? <Avatar member={answering[0]} size="sm" /> : <span className="avatar">BD</span>}
                  </span>
                  <span className="msg__body">
                    <span className="msg__bubble">
                      <span className="dim">
                        {answering[0]?.displayName.split(' ')[0] ?? 'The board'} is thinking. About a minute.
                      </span>
                    </span>
                  </span>
                </div>
              )}
            </div>

            {/* What the member actually asked the owner to do. Optional, always: a board
                that quietly signed him up to three things because it mentioned them would
                be worse than one that suggests nothing. */}
            {!thinking && actions.length > 0 && (
              <div className="asks">
                <div className="asks__label">Add to action list</div>
                {actions.map(a => (
                  <div key={a.id} className={`ask-row${a.acceptedAt ? ' is-done' : ''}`}>
                    <div className="ask-row__body">
                      <div className="ask-row__text">{a.text}</div>
                      <div className="ask-row__meta">
                        {a.dueInDays} days · {a.successMetric}
                      </div>
                    </div>
                    {a.acceptedAt ? (
                      <span className="ask-row__done">Added</span>
                    ) : (
                      <button
                        className="btn btn--small btn--brand"
                        disabled={applying !== null}
                        onClick={() => apply(a)}
                      >
                        {applying === a.id ? 'Adding' : 'Add'}
                      </button>
                    )}
                  </div>
                ))}
              </div>
            )}

            {/* A member can ask for a figure or a document, so there has to be somewhere to
                put one without leaving the conversation. */}
            {!thinking && !thread?.closed && <Attachments consultId={consultId} compact />}

            {error && <div className="signin__error">{error}</div>}

            {!thinking && thread?.canReply ? (
              <>
                <div className="reply">
                  <input
                    className="reply__box"
                    value={reply}
                    placeholder={`Answer ${answering[0]?.displayName.split(' ')[0] ?? 'them'}`}
                    onChange={e => setReply(e.target.value)}
                    onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); void send() } }}
                  />
                  <button className="btn btn--brand" disabled={sending || !reply.trim()} onClick={send}>
                    {sending ? 'Sending' : 'Send'}
                  </button>
                </div>
                {/* Said before the last turn is spent, not after. */}
                <div className="dim" style={{ marginTop: 'var(--space-1)' }}>
                  {thread.turnsLeft === 1
                    ? 'One more exchange, then this ends or goes on the table.'
                    : `${thread.turnsLeft} more exchanges, then this ends or goes on the table.`}
                </div>
              </>
            ) : !thinking && !thread?.closed ? (
              <div className="dim" style={{ marginTop: 'var(--space-2)' }}>
                That is three exchanges. Put it on the table and the whole board takes it
                on Friday, or leave it here.
              </div>
            ) : null}

            {/* §0 principle 6: two paths forward, always. */}
            {!thinking && (
              <div className="item__actions">
                <button
                  className="btn btn--brand"
                  disabled={escalating || consult?.escalatedItemId != null}
                  onClick={async () => {
                    setEscalating(true)
                    await endpoints.escalate(consultId)
                    onEscalated()
                    onClose()
                  }}
                >
                  {consult?.escalatedItemId ? 'On the table' : 'Put it on the table'}
                </button>
                <button
                  className="btn btn--quiet"
                  onClick={async () => {
                    try { await endpoints.closeConsult(consultId) } catch { /* closing twice is fine */ }
                    onClose()
                  }}
                >
                  That is enough
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  )
}

